import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavigateFunction } from 'react-router-dom'

import { welcome_gaming_playStation } from '@/assets/images'
import Container from '@/components/atoms/container/container'
import PrimaryButton from '@/components/atoms/primaryButton/primaryButton'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useOrder } from '@/providers'
import { cmsCategoryService } from '@/services/cmsCategoryService'
import { cmsProductService } from '@/services/cmsProductService'
import { cmsRegionService } from '@/services/cmsRegionService'
import { TransactionService } from '@/services/transactionService'
import type { CmsCategory } from '@/types/cms/category'
import type { CmsProduct } from '@/types/cms/product'
import type { CmsRegion } from '@/types/cms/region'
import { CmsRegionType } from '@/types/cms/region'

export type GamingSelectionTemplateProps = {
    navigate: NavigateFunction
}

export default function GamingSelectionTemplate({
    navigate
}: GamingSelectionTemplateProps) {
    const { t } = useTranslation()
    const {
        setCurrentStep,
        setCashDetected,
        state,
        addToBasket: addBasketItem,
        clearBasket
    } = useOrder()
    const [categories, setCategories] = useState<CmsCategory[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedCategory, setSelectedCategory] =
        useState<CmsCategory | null>(null)
    const [products, setProducts] = useState<CmsProduct[]>([])
    const [isProductsLoading, setIsProductsLoading] = useState(false)
    const [productsError, setProductsError] = useState<string | null>(null)
    const [regions, setRegions] = useState<CmsRegion[]>([])
    const [activeRegionId, setActiveRegionId] = useState(0)
    const [selectedProduct, setSelectedProduct] = useState<CmsProduct | null>(
        null
    )
    const [draftAmount, setDraftAmount] = useState(0)
    const shouldLockToCash =
        state.paymentMethod === 'cash' &&
        (state.cashDetected || draftAmount > 0)

    const sortedCategories = useMemo(
        () => categories.slice().sort((a, b) => a.order - b.order),
        [categories]
    )

    useEffect(() => {
        if (state.voucherType !== 'gaming') {
            navigate('/welcome-with-services', { replace: true })
            return
        }
        setCurrentStep(VoucherPurchaseStep.SELECT_GAMING_SERVICE)
    }, [navigate, setCurrentStep, state.voucherType])

    useEffect(() => {
        let isMounted = true

        TransactionService.GetVoucherAmount()
            .then((response) => {
                if (!isMounted) return
                const amount =
                    typeof response?.amount === 'number' ? response.amount : 0
                setDraftAmount(amount)
                if (amount > 0) setCashDetected(true)
            })
            .catch(() => {
                if (!isMounted) return
                setDraftAmount(0)
            })

        return () => {
            isMounted = false
        }
    }, [setCashDetected])

    useEffect(() => {
        let isMounted = true
        setIsLoading(true)
        setError(null)
        cmsCategoryService
            .getCategories()
            .then((data) => {
                if (!isMounted) return
                setCategories(data)
            })
            .catch((err: unknown) => {
                if (!isMounted) return
                const message =
                    err instanceof Error ? err.message : 'Unknown error'
                setError(message)
            })
            .finally(() => {
                if (!isMounted) return
                setIsLoading(false)
            })

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!selectedCategory?.slug) return
        let isMounted = true
        setIsProductsLoading(true)
        setProductsError(null)
        setProducts([])
        cmsProductService
            .getProducts(
                selectedCategory.slug,
                activeRegionId === 0 ? undefined : activeRegionId
            )
            .then((data) => {
                if (!isMounted) return
                setProducts(data)
            })
            .catch((err: unknown) => {
                if (!isMounted) return
                const message =
                    err instanceof Error ? err.message : 'Unknown error'
                setProductsError(message)
            })
            .finally(() => {
                if (!isMounted) return
                setIsProductsLoading(false)
            })

        return () => {
            isMounted = false
        }
    }, [selectedCategory?.slug, activeRegionId])

    useEffect(() => {
        if (!selectedCategory) return
        if (regions.length > 0) return
        let isMounted = true
        cmsRegionService
            .getRegions([CmsRegionType.GIFT_CARD, CmsRegionType.BOTH])
            .then((data) => {
                if (!isMounted) return
                setRegions(data)
            })
            .catch(() => {
                if (!isMounted) return
                setRegions([])
            })

        return () => {
            isMounted = false
        }
    }, [regions.length, selectedCategory])

    useEffect(() => {
        if (!selectedCategory) return
        const originalOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = originalOverflow
        }
    }, [selectedCategory])

    const handleCategorySelect = (category: CmsCategory) => {
        setSelectedCategory(category)
        setActiveRegionId(0)
        setSelectedProduct(null)
    }

    const handleModalClose = () => {
        setSelectedCategory(null)
        setProducts([])
        setProductsError(null)
        setIsProductsLoading(false)
        setActiveRegionId(0)
        setSelectedProduct(null)
    }

    const handleProductSelect = (product: CmsProduct) => {
        setSelectedProduct(product)
    }

    const proceedAfterSelection = () => {
        if (shouldLockToCash) {
            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
            navigate('/buy-voucher-cash', {
                state: { voucherType: state.voucherType || 'gaming' }
            })
            return
        }

        setCurrentStep(VoucherPurchaseStep.SELECT_PAYMENT_METHOD)
        navigate('/payment-method', {
            state: { voucherType: state.voucherType || 'gaming' }
        })
    }

    const handleConfirmProduct = () => {
        if (!selectedProduct) return

        // Clear basket and add only the selected product
        clearBasket()
        addBasketItem({
            id: selectedProduct.id,
            name: selectedProduct.name,
            slug: selectedProduct.slug,
            sku: selectedProduct.sku,
            price: selectedProduct.price,
            imageUrl: selectedProduct.imageUrl
        })

        proceedAfterSelection()
    }

    const handleContinueWithoutProduct = () => {
        // Clear basket and proceed without product
        clearBasket()
        proceedAfterSelection()
    }

    const renderLoadingCards = () =>
        Array.from({ length: 5 }).map((_, index) => (
            <div
                key={`loading-${index}`}
                className="gaming-selection__card is-loading">
                <div className="gaming-selection__image-placeholder" />
                <span className="gaming-selection__card-cta">
                    {t('gamingSelection.loading')}
                </span>
            </div>
        ))

    return (
        <Container isFullHeight={true} className="gaming-selection">
            <Header
                navigateBackUrl="/welcome-with-services"
                navigationBackText={t('header.back')}
            />
            <div className="gaming-selection__content">
                <h1>{t('gamingSelection.title')}</h1>
                {error && (
                    <p className="gaming-selection__error">
                        {t('gamingSelection.error')}
                    </p>
                )}
                <div className="gaming-selection__grid-wrapper">
                    <div className="gaming-selection__grid">
                        {isLoading
                            ? renderLoadingCards()
                            : sortedCategories.map((category) => {
                                  const isInactive =
                                      category.status === 'inactive'
                                  return (
                                      <button
                                          key={category.slug || category.id}
                                          className={`gaming-selection__card${isInactive ? ' is-disabled' : ''}`}
                                          onClick={() =>
                                              handleCategorySelect(category)
                                          }
                                          disabled={isInactive}>
                                          <img
                                              src={
                                                  category.imageUrl ||
                                                  welcome_gaming_playStation
                                              }
                                              alt={category.name}
                                          />
                                          <span className="gaming-selection__card-cta">
                                              {t('gamingSelection.viewOffer')}
                                          </span>
                                      </button>
                                  )
                              })}
                    </div>
                </div>
                <div className="gaming-selection__bottom-action">
                    <PrimaryButton
                        text={t('gamingSelection.continueWithoutProduct')}
                        onPress={handleContinueWithoutProduct}
                    />
                </div>
            </div>
            <Footer />
            {selectedCategory && (
                <div
                    className="gaming-selection__modal"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            handleModalClose()
                        }
                    }}>
                    <div className="gaming-selection__modal-content">
                        <div className="gaming-selection__modal-header">
                            <h2>
                                {t('gamingSelection.productsTitle', {
                                    category: selectedCategory.name
                                })}
                            </h2>
                            <button
                                className="gaming-selection__modal-close"
                                onClick={handleModalClose}
                                aria-label={t('gamingSelection.close')}>
                                ×
                            </button>
                        </div>
                        {regions.length > 0 && (
                            <div className="gaming-selection__region-select">
                                <p>{t('gamingSelection.selectRegion')}</p>
                                <div className="gaming-selection__region-buttons">
                                    <button
                                        className={`gaming-selection__region-button${activeRegionId === 0 ? ' is-active' : ''}`}
                                        onClick={() => setActiveRegionId(0)}>
                                        {t('gamingSelection.allRegions')}
                                    </button>
                                    {regions.map((region) => (
                                        <button
                                            key={region.id}
                                            className={`gaming-selection__region-button${activeRegionId === region.id ? ' is-active' : ''}`}
                                            onClick={() =>
                                                setActiveRegionId(region.id)
                                            }>
                                            {region.iconUrl ? (
                                                <img
                                                    src={region.iconUrl}
                                                    alt={region.name}
                                                />
                                            ) : (
                                                <span>{region.name}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {productsError && (
                            <p className="gaming-selection__modal-message">
                                {t('gamingSelection.productsError')}
                            </p>
                        )}
                        {isProductsLoading ? (
                            <p className="gaming-selection__modal-message">
                                {t('gamingSelection.productsLoading')}
                            </p>
                        ) : products.length === 0 ? (
                            <p className="gaming-selection__modal-message">
                                {t('gamingSelection.productsEmpty')}
                            </p>
                        ) : (
                            <>
                                <div className="gaming-selection__product-grid">
                                    {products
                                        .slice()
                                        .sort((a, b) => a.order - b.order)
                                        .map((product) => {
                                            const key =
                                                product.sku ||
                                                product.slug ||
                                                String(product.id)
                                            const isSelected =
                                                selectedProduct?.id ===
                                                product.id
                                            return (
                                                <button
                                                    key={key}
                                                    className={`gaming-selection__product-card${isSelected ? ' is-selected' : ''}`}
                                                    onClick={() =>
                                                        handleProductSelect(
                                                            product
                                                        )
                                                    }>
                                                    <img
                                                        src={
                                                            product.imageUrl ||
                                                            welcome_gaming_playStation
                                                        }
                                                        alt={product.name}
                                                    />
                                                    <div className="gaming-selection__product-info">
                                                        <span>
                                                            {product.name}
                                                        </span>
                                                        {product.price !==
                                                            undefined && (
                                                            <span className="gaming-selection__product-price">
                                                                {t(
                                                                    'gamingSelection.priceRsd',
                                                                    {
                                                                        price: product.price.toFixed(
                                                                            0
                                                                        )
                                                                    }
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            )
                                        })}
                                </div>
                                <div className="gaming-selection__modal-footer">
                                    <PrimaryButton
                                        text={t(
                                            'gamingSelection.confirmProduct'
                                        )}
                                        onPress={handleConfirmProduct}
                                        isDisabled={!selectedProduct}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Container>
    )
}
